// How We Do It / Elakay Academy -- the ONE video URL parser.
//
// Turns whatever an author pastes (a YouTube watch/short/live/embed link
// with any query parameters, a youtu.be share link, a Vimeo page / showcase
// / unlisted / player link) into { provider, id, hash, start }, and builds
// the player URL ONLY from those validated parts -- never from the raw text.
// Used by the block editor (validation message), the block renderer, the
// sandbox frame (player overlays) and the Console import analysis.

export const VIDEO_PROVIDERS = Object.freeze(["youtube", "vimeo"]);

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d{6,12}$/;
const VIMEO_HASH = /^[A-Za-z0-9]{6,20}$/;
const YOUTUBE_HOSTS = /^(?:www\.|m\.|music\.)?(?:youtube\.com|youtube-nocookie\.com)$/i;
const VIMEO_HOSTS = /^(?:www\.|player\.)?vimeo\.com$/i;

const toUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return null;
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/\//, "")}`);
  } catch {
    return null;
  }
};

// "90", "90s", "1m30s", "1h2m3s" -> seconds (null when absent/invalid).
export const parseStartTime = (value) => {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return null;
  if (/^\d+$/.test(text)) return Number(text);
  const match = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(text);
  if (!match || (!match[1] && !match[2] && !match[3])) return null;
  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
};

const parseYouTube = (url) => {
  const host = url.hostname.toLowerCase();
  let id = "";
  if (host === "youtu.be" || host === "www.youtu.be") {
    id = url.pathname.split("/").filter(Boolean)[0] || "";
  } else if (YOUTUBE_HOSTS.test(host)) {
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts[0] === "watch") id = url.searchParams.get("v") || "";
    else if (["embed", "shorts", "live", "v", "e"].includes(parts[0])) id = parts[1] || "";
    else if (!parts.length) id = url.searchParams.get("v") || "";
  } else {
    return null;
  }
  if (!YOUTUBE_ID.test(id)) return { provider: "youtube", id: "", error: "invalid-id" };
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
  const start = parseStartTime(url.searchParams.get("t") || url.searchParams.get("start") || hashParams.get("t"));
  return { provider: "youtube", id, hash: "", start };
};

const parseVimeo = (url) => {
  const host = url.hostname.toLowerCase();
  if (!VIMEO_HOSTS.test(host)) return null;
  const parts = url.pathname.split("/").filter(Boolean);
  let id = "";
  let hash = url.searchParams.get("h") || "";
  if (host.startsWith("player.")) {
    if (parts[0] === "video") id = parts[1] || "";
  } else {
    // vimeo.com/ID, /ID/HASH (unlisted), /video/ID, /channels/x/ID,
    // /groups/x/videos/ID, /showcase/x/video/ID, /album/x/video/ID
    // The video id is the LAST numeric segment (a showcase/album id may
    // come first: /showcase/1234567/video/76979871).
    let numericIndex = -1;
    parts.forEach((part, index) => {
      if (VIMEO_ID.test(part)) numericIndex = index;
    });
    if (numericIndex !== -1) {
      id = parts[numericIndex];
      const following = parts[numericIndex + 1];
      if (!hash && following && VIMEO_HASH.test(following) && !VIMEO_ID.test(following)) hash = following;
    }
  }
  if (!VIMEO_ID.test(id)) return { provider: "vimeo", id: "", error: "invalid-id" };
  if (hash && !VIMEO_HASH.test(hash)) hash = "";
  const start = parseStartTime((url.hash.match(/t=([0-9hms]+)/i) || [])[1]);
  return { provider: "vimeo", id, hash, start };
};

/**
 * @returns {{provider: "youtube"|"vimeo", id: string, hash: string, start: number|null}|{provider: string, id: "", error: string}|null}
 *   null when the text is not a YouTube/Vimeo address at all.
 */
export const parseVideoUrl = (value) => {
  const url = toUrl(value);
  if (!url || !/^https?:$/.test(url.protocol)) return null;
  return parseYouTube(url) || parseVimeo(url);
};

export const isValidVideoRef = (ref) =>
  !!ref &&
  ((ref.provider === "youtube" && YOUTUBE_ID.test(ref.id || "")) ||
    (ref.provider === "vimeo" && VIMEO_ID.test(ref.id || "") && (!ref.hash || VIMEO_HASH.test(ref.hash))));

/**
 * Player URL built only from validated parts. YouTube uses the privacy-
 * enhanced domain. Autoplay always implies mute (browsers refuse unmuted
 * autoplay anyway).
 */
export const buildVideoEmbedUrl = (ref, options = {}) => {
  if (!isValidVideoRef(ref)) return "";
  const autoplay = !!options.autoplay;
  const muted = autoplay || !!options.muted;
  const controls = options.controls !== false;
  const start = Number.isFinite(Number(options.start ?? ref.start)) ? Math.max(0, Math.floor(Number(options.start ?? ref.start))) : 0;
  if (ref.provider === "youtube") {
    const params = new URLSearchParams({
      autoplay: autoplay ? "1" : "0",
      mute: muted ? "1" : "0",
      controls: controls ? "1" : "0",
      rel: "0",
      playsinline: "1",
    });
    if (start) params.set("start", String(start));
    return `https://www.youtube-nocookie.com/embed/${ref.id}?${params.toString()}`;
  }
  const params = new URLSearchParams({
    autoplay: autoplay ? "1" : "0",
    muted: muted ? "1" : "0",
    controls: controls ? "1" : "0",
    playsinline: "1",
    dnt: "1",
  });
  if (ref.hash) params.set("h", ref.hash);
  const suffix = start ? `#t=${start}s` : "";
  return `https://player.vimeo.com/video/${ref.id}?${params.toString()}${suffix}`;
};

// Public page address of the video (fallback link "watch on YouTube/Vimeo").
export const buildVideoWatchUrl = (ref) => {
  if (!isValidVideoRef(ref)) return "";
  if (ref.provider === "youtube") return `https://www.youtube.com/watch?v=${ref.id}${ref.start ? `&t=${ref.start}s` : ""}`;
  return `https://vimeo.com/${ref.id}${ref.hash ? `/${ref.hash}` : ""}`;
};

// Static poster image (YouTube only -- Vimeo has none without its API).
export const buildVideoThumbnailUrl = (ref) =>
  ref?.provider === "youtube" && YOUTUBE_ID.test(ref.id || "") ? `https://i.ytimg.com/vi/${ref.id}/hqdefault.jpg` : "";

// Direct video files (block source "url"/"upload"): accepted extensions.
export const DIRECT_VIDEO_EXTENSIONS = Object.freeze(["mp4", "webm", "mov", "m4v", "ogv"]);

export const isDirectVideoUrl = (value) => {
  const url = toUrl(value);
  const raw = String(value || "").trim();
  const path = url && /^https?:$/.test(url.protocol) ? url.pathname : raw.startsWith("/") ? raw.split(/[?#]/)[0] : "";
  if (!path) return false;
  const extension = (path.split(".").pop() || "").toLowerCase();
  return DIRECT_VIDEO_EXTENSIONS.includes(extension);
};

// Compatibility exports (previous helpers of renderers/BlockList.jsx).
export const extractYouTubeId = (value) => {
  const ref = parseVideoUrl(value);
  return ref?.provider === "youtube" ? ref.id : "";
};

export const extractVimeoId = (value) => {
  const ref = parseVideoUrl(value);
  return ref?.provider === "vimeo" ? ref.id : "";
};
